// Camp Status Service
// Manages camp status tracking including registration, payments, forms, and communication tracking

import { supabase, handleSupabaseError } from '@/lib/supabase';

// ============================================
// TEMPORARY MOCK DATA FOR UI/UX TESTING
// TODO: Remove after UI validation complete
// ============================================
const USE_MOCK_DATA = true; // TODO: Set to false after UI testing

const TEST_USER_ID = '8de40a06-01f3-4c14-a733-501d64420a47';

const MOCK_CAMP_STATUS = [
  {
    id: 'status-1',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    camp_program_id: null,
    camp_name: 'Summer Soccer Academy',
    camp_organization: 'Elite Sports Training',
    camp_email_id: 'email-1',
    status: 'action_needed',
    is_registered: true,
    registration_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    confirmation_number: 'REG-2025-001234',
    registration_email_id: 'email-1',
    total_cost: 380.00,
    amount_paid: 190.00,
    balance_due: 190.00,
    next_payment_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    forms_required: null,
    forms_submitted: null,
    total_emails: 4,
    unread_emails: 2,
    last_email_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'status-2',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    camp_program_id: null,
    camp_name: 'Art & Creativity Workshop',
    camp_organization: 'Creative Kids Academy',
    camp_email_id: 'email-5',
    status: 'considering',
    is_registered: false,
    registration_date: null,
    confirmation_number: null,
    registration_email_id: null,
    total_cost: 450.00,
    amount_paid: null,
    balance_due: null,
    next_payment_date: null,
    forms_required: null,
    forms_submitted: null,
    total_emails: 2,
    unread_emails: 1,
    last_email_date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'status-3',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    camp_program_id: null,
    camp_name: 'STEM Explorer Camp',
    camp_organization: 'Tech Academy for Kids',
    camp_email_id: 'email-7',
    status: 'confirmed',
    is_registered: true,
    registration_date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    confirmation_number: 'STEM-2025-5678',
    registration_email_id: 'email-7',
    total_cost: 520.00,
    amount_paid: 520.00,
    balance_due: 0.00,
    next_payment_date: null,
    forms_required: null,
    forms_submitted: null,
    total_emails: 3,
    unread_emails: 1,
    last_email_date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 'status-4',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    camp_program_id: null,
    camp_name: 'Swim & Tennis Multi-Sport Camp',
    camp_organization: 'Atlanta Sports Club',
    camp_email_id: 'email-10',
    status: 'action_needed',
    is_registered: false,
    registration_date: null,
    confirmation_number: null,
    registration_email_id: null,
    total_cost: 350.00,
    amount_paid: null,
    balance_due: null,
    next_payment_date: null,
    forms_required: null,
    forms_submitted: null,
    total_emails: 2,
    unread_emails: 1,
    last_email_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
  }
];

/**
 * Camp Status Service Class
 * Handles camp status tracking operations including status updates, filtering, and automatic synchronization
 */
class CampStatusService {
  /**
   * Get or create status for a camp
   * @param {string} userId - User ID
   * @param {string} campProgramId - Camp program ID (optional, may be null)
   * @param {Object} campData - Optional camp data for creation (camp_name, camp_organization, camp_email_id)
   * @returns {Promise<Object>} Camp status object
   */
  async getOrCreateStatus(userId, campProgramId = null, campData = {}) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      console.log('🏕️ CampStatusService: getOrCreateStatus called for userId:', userId, 'campProgramId:', campProgramId);

      let existingStatus = null;

      // Try to find existing status
      if (campProgramId) {
        // First try by camp_program_id
        const { data, error } = await supabase
          .from('camp_status_tracking')
          .select('*')
          .eq('user_id', userId)
          .eq('camp_program_id', campProgramId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw handleSupabaseError(error);
        }

        if (data) {
          existingStatus = data;
        }
      }

      // If not found and we have camp_name, try by camp_name
      if (!existingStatus && campData.camp_name) {
        const { data, error } = await supabase
          .from('camp_status_tracking')
          .select('*')
          .eq('user_id', userId)
          .eq('camp_name', campData.camp_name)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw handleSupabaseError(error);
        }

        if (data) {
          existingStatus = data;
        }
      }

      // Return existing status if found
      if (existingStatus) {
        console.log('🏕️ CampStatusService: Found existing status:', existingStatus.id);
        return existingStatus;
      }

      // Create new status
      console.log('🏕️ CampStatusService: Creating new status');
      const newStatusData = {
        user_id: userId,
        camp_program_id: campProgramId || null,
        camp_name: campData.camp_name || null,
        camp_organization: campData.camp_organization || null,
        camp_email_id: campData.camp_email_id || null,
        status: 'considering',
        status_last_updated: new Date().toISOString(),
        is_registered: false,
        total_emails: 0,
        unread_emails: 0
      };

      const { data: createdStatus, error: createError } = await supabase
        .from('camp_status_tracking')
        .insert(newStatusData)
        .select()
        .single();

      if (createError) {
        throw handleSupabaseError(createError);
      }

      console.log('🏕️ CampStatusService: Created new status:', createdStatus.id);
      return createdStatus;
    } catch (error) {
      console.error('Error getting or creating camp status:', error);
      throw error;
    }
  }

  /**
   * Update camp status
   * @param {string} statusId - Status ID
   * @param {Object} updates - Update fields
   * @returns {Promise<Object>} Updated status object
   */
  async updateStatus(statusId, updates = {}) {
    try {
      if (!statusId) {
        throw new Error('Status ID is required');
      }

      console.log('✏️ CampStatusService: updateStatus called for statusId:', statusId, 'updates:', updates);

      // Get current status to check if status is changing
      const { data: currentStatus, error: fetchError } = await supabase
        .from('camp_status_tracking')
        .select('status')
        .eq('id', statusId)
        .single();

      if (fetchError) {
        throw handleSupabaseError(fetchError);
      }

      if (!currentStatus) {
        throw new Error('Camp status not found');
      }

      // Validate status if provided
      if (updates.status) {
        const validStatuses = ['considering', 'registered', 'action_needed', 'confirmed', 'past'];
        if (!validStatuses.includes(updates.status)) {
          throw new Error(`Invalid status: ${updates.status}. Must be one of: ${validStatuses.join(', ')}`);
        }
      }

      // Prepare update data
      const updateData = {
        updated_at: new Date().toISOString()
      };

      // Update status_last_updated if status is changing
      if (updates.status && updates.status !== currentStatus.status) {
        updateData.status_last_updated = new Date().toISOString();
      }

      // Copy all provided update fields
      const allowedFields = [
        'status',
        'is_registered',
        'registration_date',
        'confirmation_number',
        'registration_email_id',
        'total_cost',
        'amount_paid',
        'balance_due',
        'next_payment_date',
        'payment_method',
        'forms_required',
        'forms_submitted',
        'items_to_bring_verified',
        'total_emails',
        'last_email_date',
        'unread_emails',
        'camp_name',
        'camp_organization',
        'camp_program_id',
        'camp_email_id'
      ];

      allowedFields.forEach(field => {
        if (updates[field] !== undefined) {
          updateData[field] = updates[field];
        }
      });

      const { data: updatedStatus, error: updateError } = await supabase
        .from('camp_status_tracking')
        .update(updateData)
        .eq('id', statusId)
        .select()
        .single();

      if (updateError) {
        throw handleSupabaseError(updateError);
      }

      console.log('✏️ CampStatusService: Status updated successfully');
      return updatedStatus;
    } catch (error) {
      console.error('Error updating camp status:', error);
      throw error;
    }
  }

  /**
   * Get camps by status
   * @param {string} userId - User ID
   * @param {string} status - Status to filter by
   * @returns {Promise<Array>} Array of camp status objects
   */
  async getCampsByStatus(userId, status) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      if (!status) {
        throw new Error('Status is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA && userId === TEST_USER_ID) {
        const validStatuses = ['considering', 'registered', 'action_needed', 'confirmed', 'past'];
        if (!validStatuses.includes(status)) {
          throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(', ')}`);
        }

        let filteredStatuses = MOCK_CAMP_STATUS.filter(item => 
          item.user_id === userId && item.status === status
        );

        // Add mock email data
        filteredStatuses = filteredStatuses.map(item => ({
          ...item,
          user_camp_emails: item.camp_email_id ? [{
            id: item.camp_email_id,
            email_subject: `Email for ${item.camp_name}`,
            camp_name: item.camp_name,
            email_date: item.last_email_date,
            email_category: 'registration'
          }] : []
        }));

        // Sort by status_last_updated (descending)
        filteredStatuses.sort((a, b) => {
          const aDate = a.status_last_updated || a.last_email_date || a.created_at;
          const bDate = b.status_last_updated || b.last_email_date || b.created_at;
          return new Date(bDate) - new Date(aDate);
        });

        return filteredStatuses;
      }

      console.log('🔍 CampStatusService: getCampsByStatus called for userId:', userId, 'status:', status);

      // Validate status
      const validStatuses = ['considering', 'registered', 'action_needed', 'confirmed', 'past'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(', ')}`);
      }

      const { data, error } = await supabase
        .from('camp_status_tracking')
        .select(`
          *,
          user_camp_emails(
            id,
            email_subject,
            camp_name,
            email_date,
            email_category
          )
        `)
        .eq('user_id', userId)
        .eq('status', status)
        .order('status_last_updated', { ascending: false });

      if (error) {
        throw handleSupabaseError(error);
      }

      console.log('🔍 CampStatusService: Returning', data?.length || 0, 'camps with status:', status);
      return data || [];
    } catch (error) {
      console.error('Error getting camps by status:', error);
      throw error;
    }
  }

  /**
   * Get summary for dashboard
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Status summary counts
   */
  async getStatusSummary(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA && userId === TEST_USER_ID) {
        const summary = {
          considering: 0,
          registered: 0,
          action_needed: 0,
          confirmed: 0,
          past: 0
        };

        MOCK_CAMP_STATUS
          .filter(item => item.user_id === userId)
          .forEach(item => {
            if (summary.hasOwnProperty(item.status)) {
              summary[item.status]++;
            }
          });

        return summary;
      }

      console.log('📊 CampStatusService: getStatusSummary called for userId:', userId);

      // Get all camp statuses for user
      const { data: allStatuses, error } = await supabase
        .from('camp_status_tracking')
        .select('status')
        .eq('user_id', userId);

      if (error) {
        throw handleSupabaseError(error);
      }

      // Initialize summary
      const summary = {
        considering: 0,
        registered: 0,
        action_needed: 0,
        confirmed: 0,
        past: 0
      };

      // Count by status
      (allStatuses || []).forEach(item => {
        if (summary.hasOwnProperty(item.status)) {
          summary[item.status]++;
        }
      });

      console.log('📊 CampStatusService: Status summary:', summary);
      return summary;
    } catch (error) {
      console.error('Error getting status summary:', error);
      throw error;
    }
  }

  /**
   * Auto-update status based on email intelligence
   * Called by email processing workflow
   * @param {string} userId - User ID
   * @param {string} campProgramId - Camp program ID (optional)
   * @param {Object} emailData - Email data with intelligence
   * @returns {Promise<Object>} Updated status object
   */
  async syncStatusFromEmail(userId, campProgramId, emailData) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      if (!emailData) {
        throw new Error('Email data is required');
      }

      console.log('🔄 CampStatusService: syncStatusFromEmail called for userId:', userId, 'campProgramId:', campProgramId);

      // Get or create status
      const campData = {
        camp_name: emailData.camp_name,
        camp_organization: emailData.camp_organization,
        camp_email_id: emailData.id || emailData.email_id
      };

      const status = await this.getOrCreateStatus(userId, campProgramId || emailData.camp_program_id, campData);

      // Prepare updates based on email intelligence
      const updates = {};
      let statusChanged = false;

      // Analyze email_category to determine status updates
      const emailCategory = emailData.email_category || emailData.category;
      const extractionConfidence = emailData.extraction_confidence || emailData.confidence || 0;

      // Registration email with high confidence
      if (emailCategory === 'registration' && extractionConfidence >= 0.7) {
        if (status.status !== 'registered' && status.status !== 'confirmed') {
          updates.status = 'registered';
          statusChanged = true;
        }
        updates.is_registered = true;
        
        if (emailData.registration_date || emailData.email_date) {
          const regDate = emailData.registration_date 
            ? (emailData.registration_date instanceof Date 
                ? emailData.registration_date.toISOString().split('T')[0]
                : emailData.registration_date)
            : (emailData.email_date instanceof Date
                ? emailData.email_date.toISOString().split('T')[0]
                : emailData.email_date?.split('T')[0]);
          if (regDate) {
            updates.registration_date = regDate;
          }
        }

        if (emailData.confirmation_number) {
          updates.confirmation_number = emailData.confirmation_number;
        }

        if (emailData.id || emailData.email_id) {
          updates.registration_email_id = emailData.id || emailData.email_id;
        }
      }

      // Precamp or reminder email
      if ((emailCategory === 'precamp' || emailCategory === 'reminder') && status.status === 'considering') {
        // Check if there are action items that require attention
        const hasActionItems = emailData.has_action_items || 
                              (emailData.extracted_action_items && emailData.extracted_action_items.length > 0);
        
        if (hasActionItems) {
          updates.status = 'action_needed';
          statusChanged = true;
        }
      }

      // Action needed for registered camps with outstanding items
      if (status.status === 'registered' || status.status === 'confirmed') {
        const hasActionItems = emailData.has_action_items || 
                              (emailData.extracted_action_items && emailData.extracted_action_items.length > 0);
        
        if (hasActionItems) {
          updates.status = 'action_needed';
          statusChanged = true;
        }
      }

      // Logistics or update email - confirm if already registered
      if ((emailCategory === 'logistics' || emailCategory === 'update') && status.status === 'registered') {
        // Only upgrade to confirmed if no action items are needed
        const hasActionItems = emailData.has_action_items || 
                              (emailData.extracted_action_items && emailData.extracted_action_items.length > 0);
        
        if (!hasActionItems && status.is_registered) {
          updates.status = 'confirmed';
          statusChanged = true;
        }
      }

      // Update payment information if found
      if (emailData.camp_cost !== undefined && emailData.camp_cost !== null) {
        updates.total_cost = emailData.camp_cost;
        if (updates.amount_paid === undefined) {
          updates.amount_paid = status.amount_paid || 0;
        }
        if (updates.total_cost && updates.amount_paid !== undefined) {
          updates.balance_due = updates.total_cost - updates.amount_paid;
        }
      }

      // Update forms if action items contain form-related items
      if (emailData.extracted_action_items && Array.isArray(emailData.extracted_action_items)) {
        const formActionItems = emailData.extracted_action_items.filter(
          item => item.action_type === 'form' || item.action_type === 'registration'
        );

        if (formActionItems.length > 0) {
          // Initialize forms_required if not exists
          const formsRequired = status.forms_required || [];
          
          formActionItems.forEach(item => {
            const formName = item.form_name || item.title;
            if (formName && !formsRequired.find(f => f.name === formName)) {
              formsRequired.push({
                name: formName,
                status: 'pending',
                due_date: item.due_date || null
              });
            }
          });

          updates.forms_required = formsRequired;
        }
      }

      // Update email tracking
      updates.total_emails = (status.total_emails || 0) + 1;
      
      if (emailData.email_date) {
        const emailDate = emailData.email_date instanceof Date
          ? emailData.email_date.toISOString()
          : emailData.email_date;
        updates.last_email_date = emailDate;
      }

      if (emailData.is_read === false) {
        updates.unread_emails = (status.unread_emails || 0) + 1;
      } else if (emailData.is_read === true && status.unread_emails > 0) {
        updates.unread_emails = Math.max(0, (status.unread_emails || 0) - 1);
      }

      // Check if camp dates have passed (status → 'past')
      if (emailData.camp_dates_end) {
        const endDate = emailData.camp_dates_end instanceof Date
          ? emailData.camp_dates_end
          : new Date(emailData.camp_dates_end);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (endDate < today && status.status !== 'past') {
          updates.status = 'past';
          statusChanged = true;
        }
      }

      // Update status if there are any updates
      if (Object.keys(updates).length > 0) {
        if (statusChanged && updates.status) {
          updates.status_last_updated = new Date().toISOString();
        }

        const updatedStatus = await this.updateStatus(status.id, updates);
        console.log('🔄 CampStatusService: Status synced from email, status:', updatedStatus.status);
        return updatedStatus;
      }

      // No updates needed, return current status
      console.log('🔄 CampStatusService: No updates needed');
      return status;
    } catch (error) {
      console.error('Error syncing status from email:', error);
      throw error;
    }
  }
}

// Create singleton instance
const campStatusService = new CampStatusService();

// Export individual functions for convenience
export const getOrCreateStatus = (userId, campProgramId, campData) =>
  campStatusService.getOrCreateStatus(userId, campProgramId, campData);

export const updateStatus = (statusId, updates) =>
  campStatusService.updateStatus(statusId, updates);

export const getCampsByStatus = (userId, status) =>
  campStatusService.getCampsByStatus(userId, status);

export const getStatusSummary = (userId) =>
  campStatusService.getStatusSummary(userId);

export const syncStatusFromEmail = (userId, campProgramId, emailData) =>
  campStatusService.syncStatusFromEmail(userId, campProgramId, emailData);

export default campStatusService;
